import { orderBy } from '@progress/kendo-data-query';
import { TFunction } from 'i18next';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import { CanEditCheckbookConfig } from 'src/constants/permissionsList';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage from 'src/hooks/usePostMessage';
import useGetGlMasks, { GlCodeFormatType, GlMasksResponse } from 'src/services/useGetGlMasks';
import useGetInvalidGLCodesApi, {
  InvalidGLCodeResponseItem,
} from 'src/services/useGetInvalidGLCodesApi';
import useSaveGLMasks from 'src/services/useSaveGLMasks';
import { SetState } from 'src/types/generic';
import exportToExcel from 'src/utils/exportToExcel';
import InvalidGlCodeTable from './InvalidGlCodeTable';
import useGLCodeFormatDetailErrors from './useGLCodeFormatDetailsErrors';
import { GlCodeFormatErrors } from './useGLCodeFormatErrors';

interface CheckbookConfigurationContextType {
  pageTitle: string;
  t: TFunction<'translation', undefined>;
  glCodeFormats: GlCodeFormatType[];
  setGlCodeFormats: SetState<GlCodeFormatType[]>;
  isCheckbookConfigPageDirty: boolean;
  errors: GlCodeFormatErrors[];
  errorList: string[];
  editingGLCodeFormatDataWithErrors: GlCodeFormatType[];
  initialGlFormats: GlCodeFormatType[];
  hasErrors: boolean;
  isManagementCompany: boolean;
  areFormatsRequired: boolean | undefined;
  setAreFormatsRequired: SetState<boolean>;
  glFormatsResponse: GlMasksResponse;
  areGLFormatsDirty: boolean;
  getInvalidGLCodes: (params?: {
    glMasks?: string[];
    glMaskRequired?: boolean;
  }) => Promise<InvalidGLCodeResponseItem[]>;
  isGettingInvalidGLCodes: boolean;
  saveGLFormats: () => Promise<any>;
  isFormatRequiredFlagChanged: boolean;
  checkForInvalidGLCodesAndSave: () => Promise<void>;
  saveWithToaster: VoidFunction;
  isSaveDisable: boolean;
  saveErrorMessage: string[];
  isPageEditable: boolean;
  areGLMasksLoading: boolean;
  glMasksInitialLoading: boolean;
  warningList: string[];
  hasWarnings: boolean;
  isSavingGLFormats: boolean;
}

export const CheckbookConfigurationContext = createContext<CheckbookConfigurationContextType>(
  {} as CheckbookConfigurationContextType
);

interface CheckbookConfigurationContextProps {
  children: ReactNode;
}

const CheckbookConfigurationContextProvider: FC<CheckbookConfigurationContextProps> = ({
  children,
}) => {
  const { confirm } = useConfirm();
  const { t } = useTranslation();
  const {
    glCodeFormats,
    isManagementCompany,
    setGlCodeFormats,
    isCheckbookConfigPageDirty,
    areFormatsRequired,
    setAreFormatsRequired,
    initialGlFormats,
    glFormatsResponse,
    areGLFormatsDirty,
    isFormatRequiredFlagChanged,
    getGlCodeFormats,
    areGLMasksLoading,
    glMasksInitialLoading,
  } = useGetGlMasks();

  useEffect(() => {
    getGlCodeFormats();
  }, [getGlCodeFormats]);

  const { errors, errorList, hasErrors, warnings, warningList, hasWarnings } =
    useGLCodeFormatDetailErrors(glCodeFormats, initialGlFormats);
  const { getInvalidGLCodes, isGettingInvalidGLCodes } = useGetInvalidGLCodesApi();
  const { saveGLFormats: saveGLFormatsCall, isSavingGLFormats } = useSaveGLMasks();

  const hasCheckbookConfigPermission = usePermission({
    permissions: CanEditCheckbookConfig,
    or: true,
  });

  const isPageEditable = hasCheckbookConfigPermission && isManagementCompany;
  const pageTitle = isPageEditable ? 'View/Edit GL Code Formats' : 'View GL Code Formats';

  const editingGLCodeFormatDataWithErrors: GlCodeFormatType[] = useMemo(
    () =>
      glCodeFormats.map((item, index) => ({
        ...item,
        errors: errors?.[index],
        warnings: warnings[index],
      })),

    [errors, glCodeFormats, warnings]
  );

  const saveGLFormats = useCallback(
    async () =>
      saveGLFormatsCall({
        glMaskRequired: areFormatsRequired,
        glMasks: glCodeFormats.map((formatItem) => formatItem?.format?.toUpperCase().trim()),
      }),
    [areFormatsRequired, glCodeFormats, saveGLFormatsCall]
  );

  const downloadGLCodesExcel = useCallback((invalidGLCodes: InvalidGLCodeResponseItem[]) => {
    const columns = ['glCode', 'description', 'companyName', 'companyId', 'propertyNumber'];
    const columnMapping = {
      glCode: 'GL Code',
      description: 'Description',
      companyName: 'Company/Property Name',
      companyId: 'Company ID',
      propertyNumber: 'Property Number',
    };
    exportToExcel({
      columns,
      columnMapping,
      rows: orderBy(invalidGLCodes, [{ field: 'glCode', dir: 'asc' }]),
      fileName: 'Invalid_GL_codes_format',
    });
  }, []);

  const saveWithToaster = useCallback(async () => {
    const message = isFormatRequiredFlagChanged
      ? 'GL Code format preferences updated successfully.'
      : 'GL Code formats updated successfully.';
    saveGLFormats().then(() => {
      toast.success(message);
      getGlCodeFormats();
    });
  }, [getGlCodeFormats, isFormatRequiredFlagChanged, saveGLFormats]);

  const { isSaveDisable, saveErrorMessage } = useMemo(() => {
    const isFormatRequire = areFormatsRequired && glCodeFormats.length === 0;
    const isSaveDisable = !isCheckbookConfigPageDirty || hasErrors || isFormatRequire;
    let message: string[] = [];

    if (isFormatRequire) {
      message = ['Please add format(s) or set Require GL Code formats as No to save changes.'];
    } else if (hasErrors) {
      message = errorList;
    }
    return {
      isSaveDisable,
      saveErrorMessage: message,
    };
  }, [areFormatsRequired, errorList, glCodeFormats.length, hasErrors, isCheckbookConfigPageDirty]);

  const checkForInvalidGLCodesAndSave = useCallback(async () => {
    const invalidGLCodes = await getInvalidGLCodes({
      glMaskRequired: areFormatsRequired,
      glMasks: glCodeFormats.map((formatItem) => formatItem.format),
    });

    if (invalidGLCodes.length) {
      confirm({
        title: 'Warning - Deactivate Codes',
        content: (
          <div>
            <span>
              {invalidGLCodes.length} code(s) do not meet the required format(s). All codes not in
              the correct format will be set to inactive and can no longer be used on documents.
            </span>
            {invalidGLCodes.length > 10 ? (
              <div className="flex justify-center items-center mt-6 mb-6">
                <Button color="info" onClick={() => downloadGLCodesExcel(invalidGLCodes)}>
                  Download Codes to Review
                </Button>
              </div>
            ) : (
              <InvalidGlCodeTable dataSource={invalidGLCodes} />
            )}

            <div>
              Would you like to proceed with deactivating these codes and saving the format(s)?
            </div>
          </div>
        ),
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Confirm and Deactivate',
        cancelColor: 'info',
        confirmColor: 'info',
        width: '600px',
      }).then((isConfirmed) => {
        if (isConfirmed) {
          saveGLFormats().then(() => {
            getGlCodeFormats();
            confirm({
              title: 'Request Received',
              content: 'It may take several minutes for updates to be applied to GL Codes',
              hasNoConfirmButton: true,
              cancelButtonText: 'OK',
            });
          });
        }
      });
    } else {
      saveWithToaster();
    }
  }, [
    areFormatsRequired,
    confirm,
    downloadGLCodesExcel,
    getGlCodeFormats,
    getInvalidGLCodes,
    glCodeFormats,
    saveGLFormats,
    saveWithToaster,
  ]);
  const dirtyMessage = useMemo(
    () => ({ isDirty: isCheckbookConfigPageDirty }),
    [isCheckbookConfigPageDirty]
  );

  usePostMessage(dirtyMessage);

  return (
    <CheckbookConfigurationContext.Provider
      value={{
        pageTitle,
        t,
        glCodeFormats,
        setGlCodeFormats,
        isCheckbookConfigPageDirty,
        errors,
        errorList,
        editingGLCodeFormatDataWithErrors,
        hasErrors,
        isManagementCompany,
        areFormatsRequired,
        setAreFormatsRequired,
        initialGlFormats,
        glFormatsResponse,
        areGLFormatsDirty,
        getInvalidGLCodes,
        isGettingInvalidGLCodes,
        saveGLFormats,
        isFormatRequiredFlagChanged,
        checkForInvalidGLCodesAndSave,
        saveWithToaster,
        isSaveDisable,
        saveErrorMessage,
        isPageEditable,
        areGLMasksLoading,
        glMasksInitialLoading,
        warningList,
        hasWarnings,
        isSavingGLFormats,
      }}
    >
      {children}
    </CheckbookConfigurationContext.Provider>
  );
};

export default CheckbookConfigurationContextProvider;
